import {useMemo, useState} from 'react';
import {useHistory, useLocation, useParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {Box, Button, List, ListItem, Skeleton, SxProps, Typography} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import {useObserveViewport} from '../../../../../../../hooks';
import {useAuth} from '../../../../../../../contexts/auth/context';
import {RolePermissionType} from '../../../../../../../backend/enums/role-permission-type';
import {Role} from '../../../../../../../backend/interfaces/role';
import {RolesRepository} from '../../../../../../../repositories/roles';
import GenericHeader from '../../../../../../../components/generic-header/generic-header';
import {translations} from '../../../../../../../i18n/translations';
import {ScreenPaths} from '../../../../../../../enums';
import RoleCard from '../../../../../../../components/role-card';
import {useFetchAllItems} from '../../../../../../../hooks/fetch-all-items';
import DeleteResourceConfirmationModal from '../../../../../../../components/delete-resource-confirmation-modal';
import {useSnackBar} from '../../../../../../../contexts/snack-bar/context';

const headerSubtitleSx: SxProps = {
  fontSize: '16px',
  fontWeight: 600,
  color: 'white',
  maxWidth: {xs: '250px', sm: '100%'},
};

const listSx: SxProps = {
  position: 'absolute',
  left: 0,
  right: 0,
};

const listItemSx: SxProps = {
  paddingX: 0,
};

const RolesAndPermissionsProfessionalCaregiverOfficeScreen = () => {
  const [roleToDelete, setRoleToDelete] = useState<Role | undefined>(undefined);
  const [isDeleting, setIsDeleting] = useState(false);

  const {entityId}: {entityId: string} = useParams();

  const {isRunningMobile} = useObserveViewport();

  const {t} = useTranslation();

  const history = useHistory();

  const snackBarContext = useSnackBar();

  const location = useLocation();

  const {hasWritePermission} = useAuth();

  const canWriteRoles = hasWritePermission(RolePermissionType.Team_Permissions);

  const {
    items: unsortedRoles,
    isLoading,
    refreshItems,
  } = useFetchAllItems({
    fetchFunction: ({limit, skip}) => {
      return RolesRepository.instance.getRoles(entityId, limit, skip);
    },
  });

  const roles = useMemo(() => {
    if (!unsortedRoles) {
      return;
    }

    return unsortedRoles.sort((roleA, roleB) => {
      // By default, roles with no explict display order go last.
      // FIXME: consider removing this behavior if displayOrder becomes a mandatory field.
      let roleAOrder = roleA.displayOrder ?? Number.MIN_SAFE_INTEGER;
      let roleBOrder = roleB.displayOrder ?? Number.MIN_SAFE_INTEGER;

      return roleAOrder - roleBOrder;
    });
  }, [unsortedRoles]);

  const handleCreateNewRole = () => history.push(`${location.pathname}/create`);

  const handleEditRole = (role: Role) => history.push(`${location.pathname}/${role.id!}`);

  const handleDeleteRole = async () => {
    setIsDeleting(true);

    RolesRepository.instance
      .deleteRole(entityId, roleToDelete!.id!)
      .then(() => {
        refreshItems();
      })
      .catch(() => {
        snackBarContext.setOfficeToastNotification({
          severity: 'error',
          message: t(
            translations.office.professional_caregiver.roles_and_permissions.confirm_delete.snackbar
              .error_message
          ),
        });
      })
      .finally(() => {
        setRoleToDelete(undefined);
        setIsDeleting(false);
      });
  };

  return (
    <Box sx={{position: 'relative'}}>
      <GenericHeader
        titleCrumbs={
          isRunningMobile
            ? [
                {
                  label: t(
                    translations.office.professional_caregiver.roles_and_permissions.generic_header
                      .breadcrumbs.roles_and_permissions
                  ),
                  link: '#',
                },
              ]
            : [
                {
                  label: t(
                    translations.office.professional_caregiver.roles_and_permissions.generic_header
                      .breadcrumbs.team
                  ),
                  link: `${ScreenPaths.Office}${ScreenPaths.Office_ProfessionalCaregiver}/${entityId}${ScreenPaths.ProfessionalCaregiver_ProfessionalCaregivers}`,
                },
                {
                  label: t(
                    translations.office.professional_caregiver.roles_and_permissions.generic_header
                      .breadcrumbs.roles_and_permissions
                  ),
                  link: '#',
                },
              ]
        }
        isLoading={isLoading}
        leftColumn={
          <Typography sx={headerSubtitleSx} noWrap>
            {t(
              translations.office.professional_caregiver.roles_and_permissions.generic_header
                .subtitle
            )}
          </Typography>
        }
        rightColumn={
          <>
            {canWriteRoles && (
              <Button
                size="large"
                variant="inverted"
                startIcon={<AddIcon />}
                onClick={handleCreateNewRole}
              >
                {t(translations.office.professional_caregiver.roles_and_permissions.create)}
              </Button>
            )}
          </>
        }
      />
      <List sx={listSx}>
        {roles
          ? roles.map((role) => (
              <ListItem key={role.id} sx={listItemSx}>
                <RoleCard
                  role={role}
                  actionsEnabled={canWriteRoles}
                  onEditRoleClick={() => handleEditRole(role)}
                  onDeleteRoleClick={() => setRoleToDelete(role)}
                />
              </ListItem>
            ))
          : new Array(5).fill(1).map((_item, index) => (
              <ListItem key={`role_skeleton_${index}`} sx={listItemSx}>
                <Skeleton variant="rounded" animation="wave" sx={{width: '100%', height: '65px'}} />
              </ListItem>
            ))}
      </List>

      <DeleteResourceConfirmationModal
        id="confirm-delete-role-dialog"
        open={!!roleToDelete}
        title={t(
          translations.office.professional_caregiver.roles_and_permissions.confirm_delete.title,
          {
            name: roleToDelete?.name,
          }
        )}
        description={t(
          translations.office.professional_caregiver.roles_and_permissions.confirm_delete.copy.title
        )}
        cannotBeUndoneMessage={t(
          translations.office.professional_caregiver.roles_and_permissions.confirm_delete.copy
            .subtitle
        )}
        onCancel={() => {
          setRoleToDelete(undefined);
        }}
        onConfirm={handleDeleteRole}
        isDeleting={isDeleting}
      />
    </Box>
  );
};

export default RolesAndPermissionsProfessionalCaregiverOfficeScreen;
